import { useCallback, useState } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HoverPopover } from '@/components/ui/popover';
import type { HasMetaRouteWithFullPath } from '@/router';
import { Button } from '@/components/ui/button';
import { KEEP_ALIVE_REFRESH_STATE_KEY } from '@/layouts/BasicLayout/components/MainContent/KeepAliveOutlet';
import { UploadTaskBadge } from '@/features/material-upload';

/** 展开态角标：靠右并与 chevron（右内边距 8px + 16px 宽）留出 8px 间距 */
const EXPANDED_BADGE_CLASS = 'absolute top-1/2 right-8 -translate-y-1/2';
/**
 * 折叠态角标：贴菜单按钮右上角。
 * 不能贴图标 —— 折叠态图标只有 14px，而角标最小 16px，贴着会把图标盖住。
 */
const COLLAPSED_BADGE_CLASS = 'absolute top-0 right-0';

interface NavMainProps {
  routes: HasMetaRouteWithFullPath[];
  location: Location;
  navigate: NavigateFunction;
  /**
   * 菜单角标数字，key 为菜单项的 fullPath（支持父/子任意层级）。
   * 值为 0 / undefined 时不渲染角标。
   */
  badgeCounts?: Record<string, number>;
}

export function NavMain({ routes, location, navigate, badgeCounts }: NavMainProps) {
  // 取某个菜单路径对应的角标数字（无则视为 0，不渲染）
  const getBadgeCount = useCallback(
    (path?: string) => (path ? (badgeCounts?.[path] ?? 0) : 0),
    [badgeCounts]
  );

  // 收集路由自身 + 所有有 meta 的子路由 fullPath（用于角标判断）
  const collectAllPathEntries = useCallback((route: HasMetaRouteWithFullPath): string[] => {
    const paths: string[] = [];
    const traverse = (r: HasMetaRouteWithFullPath) => {
      r.children?.forEach((child) => {
        if (child.meta) {
          const c = child as HasMetaRouteWithFullPath;
          if (c.fullPath) paths.push(c.fullPath);
          if (c.children) traverse(c);
        }
      });
    };
    traverse(route);
    return paths;
  }, []);

  // 判读某个父菜单自身或其任意子项是否有角标
  const hasBadge = useCallback(
    (route: HasMetaRouteWithFullPath): boolean => {
      if (getBadgeCount(route.fullPath) > 0) return true;
      return collectAllPathEntries(route).some((p) => getBadgeCount(p) > 0);
    },
    [collectAllPathEntries, getBadgeCount]
  );

  // 计算父菜单角标：自身角标 + 所有子项角标之和（用于折叠态无子菜单列表时的总量提示）
  const sumBadgeCount = useCallback(
    (route: HasMetaRouteWithFullPath): number => {
      const own = getBadgeCount(route.fullPath);
      const childSum = collectAllPathEntries(route).reduce((sum, p) => sum + getBadgeCount(p), 0);
      return own + childSum;
    },
    [collectAllPathEntries, getBadgeCount]
  );

  // 管理每个菜单项的展开状态
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const { state, isMobile } = useSidebar(); // 获取侧边栏状态和移动端状态
  /** 桌面端折叠态（图标 + 文字竖排），角标需要贴图标而不是靠右 */
  const isCollapsedIconMode = state === 'collapsed' && !isMobile;

  // 将动态路由模式转换为正则表达式进行匹配
  const matchDynamicRoute = useCallback((routePath: string, currentPath: string): boolean => {
    // 将路由模式中的 :param 转换为正则表达式
    const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(currentPath);
  }, []);

  // 判断路径是否匹配（支持精确匹配、前缀匹配和动态路由匹配）
  const isPathMatch = useCallback(
    (routePath: string, currentPath: string): boolean => {
      // 精确匹配
      if (currentPath === routePath) return true;
      // 动态路由匹配
      if (routePath.includes(':') && matchDynamicRoute(routePath, currentPath)) return true;
      // 前缀匹配：当前路径以路由路径开头（确保是完整的路径段）
      if (currentPath.startsWith(routePath + '/')) return true;
      return false;
    },
    [matchDynamicRoute]
  );

  // 判断菜单项是否激活
  const isActive = useCallback(
    (url: string) => {
      return isPathMatch(url, location.pathname);
    },
    [location.pathname, isPathMatch]
  );

  // 递归收集所有子路由的路径
  const collectAllChildPaths = useCallback((route: HasMetaRouteWithFullPath): string[] => {
    const paths: string[] = [];

    const traverse = (r: HasMetaRouteWithFullPath) => {
      if (r.fullPath) {
        paths.push(r.fullPath);
      }
      if (r.children) {
        // 过滤出有meta的子路由
        r.children.forEach((child) => {
          if (child.meta) {
            traverse(child as HasMetaRouteWithFullPath);
          }
        });
      }
    };

    if (route.children) {
      // 过滤出有meta的子路由
      route.children.forEach((child) => {
        if (child.meta) {
          traverse(child as HasMetaRouteWithFullPath);
        }
      });
    }

    return paths;
  }, []);

  // 判断父菜单是否有子项处于激活状态（递归检查所有层级）
  const hasActiveChild = useCallback(
    (route: HasMetaRouteWithFullPath): boolean => {
      if (!route.children) return false;

      // 收集所有子路由路径
      const allChildPaths = collectAllChildPaths(route);

      // 检查当前路径是否匹配任何子路由路径
      return allChildPaths.some((childPath) => isPathMatch(childPath, location.pathname));
    },
    [location.pathname, collectAllChildPaths, isPathMatch]
  );

  // 处理菜单点击
  const handleMenuClick = useCallback(
    (url: string) => {
      navigate(url, {
        state: {
          [KEEP_ALIVE_REFRESH_STATE_KEY]: true,
        },
      });
    },
    [navigate]
  );

  // 切换菜单展开状态
  const toggleItem = useCallback((key: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {routes.map((route) => {
            const hasChildren = route.children && route.children.length > 0;
            const isParentActive = hasActiveChild(route);
            const itemKey = route.fullPath || route.meta.title;
            const Icon = route.meta.icon;

            if (hasChildren) {
              // 只在桌面端折叠状态下使用HoverPopover，移动端抽屉使用正常的展开模式
              if (state === 'collapsed' && !isMobile) {
                const popoverContent = (
                  <div className="space-y-1">
                    {route.children!.map((subRoute) => {
                      if (!subRoute.meta) return null;
                      const subUrl = subRoute.fullPath;
                      const childBadge = getBadgeCount(subUrl);
                      return (
                        <Button
                          size="sm"
                          variant={isActive(subUrl) ? 'default' : 'ghost'}
                          key={subUrl}
                          onClick={() => handleMenuClick(subUrl)}
                          className="w-full px-2 py-1.5 text-sm justify-normal"
                        >
                          <span className="flex-1 text-left">{subRoute.meta.title}</span>
                          <UploadTaskBadge count={childBadge} className="ml-2 shrink-0" />
                        </Button>
                      );
                    })}
                  </div>
                );

                return (
                  <SidebarMenuItem key={itemKey}>
                    <HoverPopover
                      content={popoverContent}
                      side="right"
                      align="start"
                      sideOffset={8}
                      contentClassName="w-48 p-2"
                      delayDuration={300}
                    >
                      <SidebarMenuButton
                        className="group-data-[collapsible=compact]:flex-col group-data-[collapsible=compact]:items-center group-data-[collapsible=compact]:justify-center group-data-[collapsible=compact]:gap-1 group-data-[collapsible=compact]:py-2 group-data-[collapsible=compact]:px-2 group-data-[collapsible=compact]:text-xs group-data-[collapsible=compact]:h-15"
                        isActive={isParentActive}
                        tooltip={route.meta.title}
                        onClick={() => handleMenuClick(route.fullPath)}
                      >
                        {Icon && <Icon className="h-4 w-4" />}
                        <span className="group-data-[collapsible=compact]:text-center group-data-[collapsible=compact]:leading-tight">
                          {route.meta.title}
                        </span>
                      </SidebarMenuButton>
                    </HoverPopover>
                    {/* 折叠态无法展开子菜单，父项角标用「自身 + 子项」汇总数贴按钮右上角 */}
                    <UploadTaskBadge
                      count={hasBadge(route) ? sumBadgeCount(route) : 0}
                      className={COLLAPSED_BADGE_CLASS}
                    />
                  </SidebarMenuItem>
                );
              }

              // 展开模式下的原有逻辑
              return (
                <Collapsible
                  key={itemKey}
                  open={openItems[itemKey] || isParentActive}
                  onOpenChange={() => toggleItem(itemKey)}
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        className="text-sidebar-foreground text-nowrap"
                        isActive={isParentActive}
                        isRootMenu={false}
                      >
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{route.meta.title}</span>
                        <ChevronRight
                          className={`ml-auto h-4 w-4 transition-transform ${
                            openItems[itemKey] || isParentActive ? 'rotate-90' : ''
                          }`}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    {/* 父项自身角标（子项角标在各自子菜单项上单独展示） */}
                    <UploadTaskBadge
                      count={getBadgeCount(route.fullPath)}
                      className={EXPANDED_BADGE_CLASS}
                    />
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {route.children!.map((subRoute) => {
                          if (!subRoute.meta) return null;
                          const subUrl = subRoute.fullPath;
                          const childBadge = getBadgeCount(subUrl);
                          const AsideBarWrapper = subRoute.meta.asideBarWrapper;
                          const SubButton = (
                            <SidebarMenuSubButton
                              isActive={isActive(subUrl)}
                              onClick={() => handleMenuClick(subUrl)}
                              // 角标绝对定位在右侧，给按钮右侧留出空间避免文字被压
                              className={childBadge > 0 ? '!pr-10' : undefined}
                            >
                              <span>{subRoute.meta.title}</span>
                            </SidebarMenuSubButton>
                          );
                          return (
                            <SidebarMenuSubItem key={subUrl}>
                              {AsideBarWrapper ? (
                                <AsideBarWrapper>{SubButton}</AsideBarWrapper>
                              ) : (
                                SubButton
                              )}
                              <UploadTaskBadge
                                count={childBadge}
                                className="absolute top-1/2 right-2 -translate-y-1/2"
                              />
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              );
            }

            // 无子菜单的一级菜单项
            const url = route.fullPath;
            const AsideBarWrapper = route.meta.asideBarWrapper;
            const urlBadge = getBadgeCount(url);
            const menuButton = (
              <SidebarMenuButton
                className="group-data-[collapsible=compact]:flex-col group-data-[collapsible=compact]:items-center group-data-[collapsible=compact]:justify-center group-data-[collapsible=compact]:gap-1 group-data-[collapsible=compact]:py-2 group-data-[collapsible=compact]:px-2 group-data-[collapsible=compact]:text-xs group-data-[collapsible=compact]:h-15"
                tooltip={route.meta.title}
                isActive={isActive(url)}
                onClick={() => handleMenuClick(url)}
              >
                {Icon && <Icon />}
                <span className="group-data-[collapsible=compact]:text-center group-data-[collapsible=compact]:leading-tight">
                  {route.meta.title}
                </span>
              </SidebarMenuButton>
            );
            return (
              <SidebarMenuItem key={itemKey}>
                {AsideBarWrapper ? <AsideBarWrapper>{menuButton}</AsideBarWrapper> : menuButton}
                {/* 折叠态贴按钮右上角，展开态靠右，与有子菜单的父项口径一致 */}
                <UploadTaskBadge
                  count={urlBadge}
                  className={isCollapsedIconMode ? COLLAPSED_BADGE_CLASS : EXPANDED_BADGE_CLASS}
                />
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
